import React, { useState } from "react";
import { api, formatApiError } from "../api/client";

export const ForgotPasswordPage = ({ onBackToLogin }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (ex) {
      setErr(formatApiError(ex) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--surface-page)" }} data-testid="forgot-password-page">
      <div className="w-full max-w-md rounded-lg p-7" style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", boxShadow: "var(--shadow-card)" }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold text-sm" style={{ background: "var(--brand-primary)" }}>400</div>
          <div>
            <h1 className="text-lg font-semibold" style={{ color: "var(--ink-primary)" }}>Four Hundred Club</h1>
            <p className="text-xs" style={{ color: "var(--ink-tertiary)" }}>Red Hook Rhinebeck Elks Lodge #2022</p>
          </div>
        </div>

        {sent ? (
          <div className="mt-6" data-testid="forgot-password-sent">
            <h2 className="text-base font-semibold mb-2" style={{ color: "var(--ink-primary)" }}>Check your email</h2>
            <p className="text-sm" style={{ color: "var(--ink-tertiary)" }}>
              If an account exists for <strong style={{ color: "var(--ink-primary)" }}>{email}</strong>, we've sent a link to reset your password. It's valid for 1 hour.
            </p>
            <p className="mt-3 text-xs" style={{ color: "var(--ink-tertiary)" }}>
              Didn't get it? Check your spam folder, or wait a minute and try again.
            </p>
            <button
              type="button"
              onClick={onBackToLogin}
              data-testid="forgot-back-to-login"
              className="mt-6 text-sm font-medium"
              style={{ color: "var(--brand-primary)" }}
            >
              ← Back to sign in
            </button>
          </div>
        ) : (
          <>
            <p className="mt-5 mb-4 text-sm" style={{ color: "var(--ink-tertiary)" }}>
              Enter your email. We'll send you a link to reset your password.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>Email</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                  style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                />
              </div>
              {err && <p data-testid="forgot-error" className="text-sm font-medium" style={{ color: "var(--semantic-danger)" }}>{err}</p>}
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={busy}
                className="w-full px-5 py-2.5 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
                style={{ background: "var(--brand-primary)" }}
              >
                {busy ? "Sending\u2026" : "Send reset link"}
              </button>
            </form>
            <button
              type="button"
              onClick={onBackToLogin}
              data-testid="forgot-back-to-login"
              className="mt-6 block text-sm font-medium mx-auto"
              style={{ color: "var(--brand-primary)" }}
            >
              ← Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const ResetPasswordPage = ({ token, onSuccess, onBackToLogin }) => {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 6) { setErr("New password must be at least 6 characters."); return; }
    if (next !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(onSuccess, 1500);
    } catch (ex) {
      setErr(formatApiError(ex) || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--surface-page)" }} data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-lg p-7" style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", boxShadow: "var(--shadow-card)" }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold text-sm" style={{ background: "var(--brand-primary)" }}>400</div>
          <div>
            <h1 className="text-lg font-semibold" style={{ color: "var(--ink-primary)" }}>Four Hundred Club</h1>
            <p className="text-xs" style={{ color: "var(--ink-tertiary)" }}>Red Hook Rhinebeck Elks Lodge #2022</p>
          </div>
        </div>

        {done ? (
          <div className="mt-6" data-testid="reset-password-success">
            <h2 className="text-base font-semibold mb-2" style={{ color: "var(--ink-primary)" }}>Password updated</h2>
            <p className="text-sm" style={{ color: "var(--ink-tertiary)" }}>Redirecting you to sign in\u2026</p>
          </div>
        ) : (
          <>
            <p className="mt-5 mb-4 text-sm" style={{ color: "var(--ink-tertiary)" }}>Choose a new password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>New password</label>
                <input
                  data-testid="reset-pw-new"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                  style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                />
                <p className="mt-1 text-xs" style={{ color: "var(--ink-tertiary)" }}>At least 6 characters.</p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>Confirm new password</label>
                <input
                  data-testid="reset-pw-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                  style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                />
              </div>
              {err && <p data-testid="reset-error" className="text-sm font-medium" style={{ color: "var(--semantic-danger)" }}>{err}</p>}
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={busy}
                className="w-full px-5 py-2.5 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
                style={{ background: "var(--brand-primary)" }}
              >
                {busy ? "Updating\u2026" : "Update password"}
              </button>
            </form>
            <button
              type="button"
              onClick={onBackToLogin}
              data-testid="reset-back-to-login"
              className="mt-6 block text-sm font-medium mx-auto"
              style={{ color: "var(--brand-primary)" }}
            >
              ← Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
};
